import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ScanLine, X, Plus } from 'lucide-react';
import { SEL_API } from '../../lib/selectionShare';

const MAX_IMAGES = 5;

// Screenshots chosen so far (same slip): add more, remove, then read them as one selection
const ScreenshotQueue = ({ files, reading, onAdd, onRemove, onRead }) => (
  <div data-testid="import-selection-queue">
    <div className="grid grid-cols-3 gap-2 mb-3">
      {files.map((f, i) => (
        <div key={`${f.name}-${i}`} className="relative rounded-xl overflow-hidden border border-white/10 aspect-[9/16] bg-black/30">
          <img src={URL.createObjectURL(f)} alt={`Screenshot ${i + 1}`} className="w-full h-full object-cover object-top" />
          <span className="absolute bottom-1 left-1 text-[10px] font-black bg-[#F5A623] text-[#1A1A1A] rounded px-1">{i + 1}</span>
          {!reading && (
            <button type="button" onClick={() => onRemove(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center" data-testid={`import-selection-remove-${i}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      {files.length < MAX_IMAGES && !reading && (
        <button type="button" onClick={onAdd} className="rounded-xl border border-dashed border-[#F5A623]/50 text-[#F5A623] aspect-[9/16] flex flex-col items-center justify-center gap-1 text-xs font-bold" data-testid="import-selection-add-more">
          <Plus className="w-5 h-5" /> Aggiungi
        </button>
      )}
    </div>
    <button type="button" onClick={onRead} disabled={reading} className="w-full py-3 rounded-xl bg-[#F5A623] text-[#1A1A1A] font-black flex items-center justify-center gap-2" data-testid="import-selection-read">
      {reading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
      {reading ? `Leggo ${files.length > 1 ? `${files.length} screenshot` : 'lo screenshot'}…` : `Leggi selezione (${files.length} ${files.length > 1 ? 'immagini' : 'immagine'})`}
    </button>
  </div>
);

// Daily automatic reads used up: the user can still build the selection by hand
const LimitReached = ({ message, onCreate }) => (
  <div className="rounded-2xl border border-[#F5A623]/40 bg-[#F5A623]/10 p-4 mb-3" data-testid="import-selection-limit">
    <p className="text-sm text-white font-bold mb-3" data-testid="import-selection-limit-message">{message}</p>
    <button type="button" onClick={onCreate} className="w-full py-2.5 rounded-xl bg-[#F5A623] text-[#1A1A1A] font-black flex items-center justify-center gap-2" data-testid="import-selection-limit-create">
      <Plus className="w-4 h-4" /> Crea
    </button>
  </div>
);

// "Importa da screenshot": read 1-5 screenshots, then the selection page opens with what was read
export const ImportSelectionSheet = ({ onClose, onRead, onCreate }) => {
  const inputRef = useRef(null);
  const [limitMsg, setLimitMsg] = useState('');
  const [reading, setReading] = useState(false);
  const [files, setFiles] = useState([]);

  // Copy the FileList now: the input is reset right after, which empties the live list before a lazy updater runs
  const addFiles = (list) => setFiles((cur) => [...cur, ...Array.from(list || [])].slice(0, MAX_IMAGES));

  const read = async () => {
    if (!files.length) return;
    setReading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('images', f));
      const { data } = await axios.post(`${SEL_API}/read`, fd, { timeout: 90000 * Math.max(1, files.length) });
      if (data.unrecognized?.length) toast.warning(`Non riconosciute: ${data.unrecognized.join(', ')}`);
      onRead(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'vip_limit' || d?.code === 'paused') setLimitMsg(d.message);
      else toast.error(typeof d === 'string' ? d : d?.message || 'Lettura non riuscita, riprova');
    } finally {
      setReading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9990] bg-black/70 flex items-end sm:items-center justify-center" data-testid="import-selection-sheet">
      <div className="w-full max-w-md bg-[#1A1A1A] rounded-t-3xl sm:rounded-3xl border border-white/10 p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-black text-lg">Importa da screenshot</h3>
          <button type="button" onClick={onClose} className="p-1 text-gray-400" data-testid="import-selection-close"><X className="w-5 h-5" /></button>
        </div>
        {limitMsg && <LimitReached message={limitMsg} onCreate={onCreate} />}
        {!files.length && !limitMsg && (
          <button type="button" onClick={() => inputRef.current?.click()}
            className="w-full py-8 rounded-2xl border border-dashed border-[#F5A623]/50 text-[#F5A623] font-bold flex flex-col items-center gap-2" data-testid="import-selection-pick-file">
            <ScanLine className="w-6 h-6" />
            Carica gli screenshot della selezione
            <span className="text-xs font-normal text-gray-400">Schedina lunga? Scegli più immagini insieme</span>
          </button>
        )}
        {files.length > 0 && !limitMsg && (
          <ScreenshotQueue files={files} reading={reading} onAdd={() => inputRef.current?.click()}
            onRemove={(i) => setFiles((cur) => cur.filter((_, j) => j !== i))} onRead={read} />
        )}
        <input ref={inputRef} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="import-selection-file-input" />
      </div>
    </div>
  );
};

export default ImportSelectionSheet;
